import type React from 'react';
import { useEffect, useMemo } from 'react';
import { useGate, useStore } from 'effector-react';
import { useInfiniteQuery, useQuery } from 'react-query';
import { API_DOMAIN, PAGE_SIZE } from '@constants';
import {
  type LibAlbumResp,
  type LibItemAlbum,
  type LibItemModal3d,
  SelectStatus,
} from '@types';
import ItemList, { type ItemListRowProps } from '@components/ItemList';
import axios from '@utils/axios';
import Checkbox from '@components/Checkbox';
import getSelectedStatus from '@utils/getSelectedStatus';
import { $libSelected, $libSelectedApi } from '../stores/libSelected';
import LibModalList from '../layouts/LibModalList';
import LibModalListHeader from '../layouts/LibModalListHeader';
import LibModalListBody from '../layouts/LibModalListBody';
import { $libAlbumId, LibItemsGate, LibMultipleGate } from '../stores/libState';
import AlbumPath from '../components/AlbumPath';

type Props = {
  queryKey: string;
  row: React.ComponentType<
    ItemListRowProps<{
      albums: LibItemAlbum[];
      items: LibItemModal3d[];
    }>
  >;
};

function FetchList({ queryKey, row }: Props) {
  const selected = useStore($libSelected);
  const multiple = useStore(LibMultipleGate.state);
  const albumId = useStore($libAlbumId);

  useEffect(() => $libSelectedApi.clear, []);

  const { data: aData } = useQuery(
    [queryKey, 'albums', albumId],
    () => {
      const url = `${API_DOMAIN}/member/my/3dmodel/albums`;
      const params = { albumId };
      return axios.get<LibAlbumResp>(url, { params });
    },
    {
      refetchOnWindowFocus: true,
    }
  );

  const {
    data: iData,
    isLoading,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery(
    [queryKey, 'items', albumId],
    ({ pageParam = 1 }) => {
      const url = `${API_DOMAIN}/member/my/3dmodels`;
      const params = { albumId, page: pageParam };
      return axios.get<LibItemModal3d[]>(url, { params });
    },
    {
      refetchOnWindowFocus: true,
      getNextPageParam: (lastPage, allPages) => {
        if (lastPage.length < PAGE_SIZE) return undefined;
        return allPages.length + 1;
      },
    }
  );

  const items = useMemo(
    () => (iData?.pages ?? []).reduce((acc, cur) => acc.concat(cur), []),
    [iData?.pages]
  );
  const { children: albums = [], parentId, name: albumName } = aData ?? {};

  useGate(LibItemsGate, items);

  return (
    <LibModalList>
      <LibModalListHeader>
        {multiple && (
          <Checkbox
            className="mr-20"
            status={getSelectedStatus(selected.length, items.length)}
            onClick={(status) => {
              if (status === SelectStatus.all) {
                $libSelectedApi.clear();
              } else {
                $libSelectedApi.set(items.map((x) => x.id));
              }
            }}
          />
        )}
        <AlbumPath
          albumId={albumId}
          albumName={albumName}
          parentId={parentId}
        />
      </LibModalListHeader>
      <LibModalListBody>
        <ItemList
          itemCount={items.length + albums.length}
          itemData={{ items, albums }}
          hasNextPage={hasNextPage}
          loading={isLoading}
          loadMoreItems={async () => {
            fetchNextPage();
          }}
          row={row}
        />
      </LibModalListBody>
    </LibModalList>
  );
}

export default FetchList;
